import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CloudUpload,
  GripVertical,
  Trash
} from 'react-bootstrap-icons';
import { Button, Form, Modal, Tab, Tabs } from 'react-bootstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../../helpers/api';
import {
  API_URL,
  APP_ROLES,
  ENDPOINTS,
  ROLE_ADMIN,
  ROLE_INSTALLER,
  ROLE_INVENTORY_MANAGER,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SERVICE,
  ROLE_SUPER_ADMIN,
  ROLE_TECH
} from '../../../helpers/constants';
import AlertModal from '../../AlertModal';
import CourseImageThumb from './CourseImageThumb';
import AcademyHome from './AcademyHome';
import { ACADEMY_TOPICS } from './academyTopics';

const COURSE_ROLES_IMPLICIT_ACCESS = [ROLE_SUPER_ADMIN, ROLE_ADMIN];

function stripImplicitCourseAccessRoles(roles) {
  if (!Array.isArray(roles)) return [];
  return roles.filter(r => !COURSE_ROLES_IMPLICIT_ACCESS.includes(r));
}

function CourseTrainingCatalog({
  courses,
  setCourses,
  onManageCourse,
  onViewCourse,
  canManageCourses,
  onReloadList,
  listLoading = false,
  listError = '',
  listSearchInput = '',
  onListSearchInputChange
}) {
  const [selectPrimaryBg, setSelectPrimaryBg] = useState('#007bff');
  const [courseModalMode, setCourseModalMode] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseModalTab, setCourseModalTab] = useState('details');
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [createCategory, setCreateCategory] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCoverFile, setEditCoverFile] = useState(null);
  const [editThumbFile, setEditThumbFile] = useState(null);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createCoverFile, setCreateCoverFile] = useState(null);
  const [createThumbFile, setCreateThumbFile] = useState(null);
  const [createRoles, setCreateRoles] = useState([]);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [deletingCourse, setDeletingCourse] = useState(false);
  const [showDeleteCourseConfirm, setShowDeleteCourseConfirm] = useState(false);
  const [showArrangeModal, setShowArrangeModal] = useState(false);
  const [arrangeCourses, setArrangeCourses] = useState([]);
  const [savingArrangeOrder, setSavingArrangeOrder] = useState(false);

  const createCoverInputRef = useRef(null);
  const createThumbInputRef = useRef(null);
  const editCoverInputRef = useRef(null);
  const editThumbInputRef = useRef(null);

  function getRoleInfo(roleName) {
    if (!roleName) return null;
    const color =
      {
        [ROLE_SUPER_ADMIN]: '#850131',
        [ROLE_ADMIN]: '#fd7e14',
        [ROLE_SALES]: '#2563eb',
        [ROLE_SALES_MANAGER]: '#6f42c1',
        [ROLE_SERVICE]: '#059669',
        [ROLE_TECH]: '#0dcaf0',
        [ROLE_INSTALLER]: '#d97706',
        [ROLE_INVENTORY_MANAGER]: '#20c997',
        [ROLE_SALES_AND_INVENTORY_MANAGER]: '#6610f2'
      }[roleName] || '#6c757d';
    return { id: roleName, name: roleName, color };
  }

  const createCoverPreviewUrl = useMemo(() => (createCoverFile ? URL.createObjectURL(createCoverFile) : null), [
    createCoverFile
  ]);
  const createThumbPreviewUrl = useMemo(() => (createThumbFile ? URL.createObjectURL(createThumbFile) : null), [
    createThumbFile
  ]);
  const editCoverPreviewUrl = useMemo(() => (editCoverFile ? URL.createObjectURL(editCoverFile) : null), [
    editCoverFile
  ]);
  const editThumbPreviewUrl = useMemo(() => (editThumbFile ? URL.createObjectURL(editThumbFile) : null), [
    editThumbFile
  ]);

  const courseAccessRoleOptions = useMemo(
    () =>
      APP_ROLES.filter(name => !COURSE_ROLES_IMPLICIT_ACCESS.includes(name)).map(name => {
        const info = getRoleInfo(name);
        return { value: name, label: name, color: info.color };
      }),
    []
  );

  const selectStyles = useMemo(
    () => ({
      control: base => ({
        ...base,
        borderColor: '#ced4da',
        boxShadow: 'none',
        '&:hover': { borderColor: '#adb5bd' },
        minHeight: 56,
        maxHeight: 56,
        height: 56,
        borderRadius: '1.2rem',
        alignItems: 'center'
      }),
      valueContainer: base => ({
        ...base,
        minHeight: 42,
        maxHeight: 42,
        height: 42,
        overflowY: 'auto',
        flexWrap: 'wrap',
        alignItems: 'center',
        paddingTop: 2,
        paddingBottom: 2
      }),
      indicatorsContainer: base => ({
        ...base,
        alignSelf: 'center',
        height: 'auto'
      }),
      menu: base => ({
        ...base,
        zIndex: 2060
      }),
      menuPortal: base => ({
        ...base,
        zIndex: 2060
      }),
      menuList: base => ({
        ...base,
        maxHeight: 220,
        paddingTop: 4,
        paddingBottom: 4
      }),
      option: (base, state) => ({
        ...base,
        cursor: 'pointer',
        padding: '10px 14px',
        ...(state.isSelected
          ? { backgroundColor: selectPrimaryBg, color: '#fff' }
          : {
              backgroundColor: '#fff',
              color: '#212529',
              ...(state.isFocused ? { boxShadow: 'inset 3px 0 0 0 #850131' } : {})
            })
      }),
      multiValue: base => ({
        ...base,
        backgroundColor: '#e9ecef',
        borderRadius: '0.35rem'
      }),
      multiValueLabel: base => ({
        ...base,
        color: '#495057',
        fontWeight: 500
      }),
      multiValueRemove: base => ({
        ...base,
        color: '#6c757d',
        ':hover': { backgroundColor: '#dee2e6', color: '#343a40' }
      })
    }),
    [selectPrimaryBg]
  );

  const selectedRoleOptions = useMemo(
    () => selectedRoleIds.map(id => courseAccessRoleOptions.find(opt => opt.value === id)).filter(Boolean),
    [selectedRoleIds, courseAccessRoleOptions]
  );

  useLayoutEffect(() => {
    const probe = document.createElement('div');
    probe.className = 'bg-primary';
    probe.setAttribute(
      'style',
      'position:absolute;left:-9999px;top:0;width:1px;height:1px;visibility:hidden;pointer-events:none'
    );
    document.body.appendChild(probe);
    const { backgroundColor } = getComputedStyle(probe);
    document.body.removeChild(probe);
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      setSelectPrimaryBg(backgroundColor);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (createCoverPreviewUrl) URL.revokeObjectURL(createCoverPreviewUrl);
    };
  }, [createCoverPreviewUrl]);
  useEffect(() => {
    return () => {
      if (createThumbPreviewUrl) URL.revokeObjectURL(createThumbPreviewUrl);
    };
  }, [createThumbPreviewUrl]);
  useEffect(() => {
    return () => {
      if (editCoverPreviewUrl) URL.revokeObjectURL(editCoverPreviewUrl);
    };
  }, [editCoverPreviewUrl]);
  useEffect(() => {
    return () => {
      if (editThumbPreviewUrl) URL.revokeObjectURL(editThumbPreviewUrl);
    };
  }, [editThumbPreviewUrl]);

  const resetCreateWizard = () => {
    setCreateCategory('');
    setCreateTitle('');
    setCreateDescription('');
    setCreateCoverFile(null);
    setCreateThumbFile(null);
    setCreateRoles([]);
  };

  const openCourseModal = (course, tab) => {
    setCourseModalMode('manage');
    setSelectedCourse(course);
    setCourseModalTab(tab);
    setSelectedRoleIds(stripImplicitCourseAccessRoles(course.allowedRoles || []));
    setEditCategory(course.category || '');
    setEditTitle(course.title || '');
    setEditDescription(course.description || '');
    setEditCoverFile(null);
    setEditThumbFile(null);
  };

  const openCreateCourseModal = () => {
    resetCreateWizard();
    setCourseModalMode('create');
    setSelectedCourse(null);
  };

  const closeCourseModal = () => {
    setCourseModalMode(null);
    setSelectedCourse(null);
    setCourseModalTab('details');
    setSelectedRoleIds([]);
    setEditTitle('');
    setEditDescription('');
    setEditCoverFile(null);
    setEditThumbFile(null);
    setDeletingCourse(false);
    setShowDeleteCourseConfirm(false);
    resetCreateWizard();
  };

  const openArrangeModal = () => {
    setArrangeCourses(courses.map(course => ({ ...course })));
    setShowArrangeModal(true);
  };

  const closeArrangeModal = () => {
    if (savingArrangeOrder) return;
    setShowArrangeModal(false);
    setArrangeCourses([]);
  };

  const onArrangeDragEnd = result => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    setArrangeCourses(prev => {
      const next = [...prev];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  };

  const handleSaveArrangeCourses = async () => {
    const orderedIds = arrangeCourses.map(course => String(course._id ?? course.id ?? '')).filter(Boolean);
    if (orderedIds.length !== courses.length) {
      toast.error('Could not arrange courses');
      return;
    }
    setSavingArrangeOrder(true);
    const { error } = await makeApiRequests({
      method: 'PUT',
      endpoint: ENDPOINTS.COURSES_REORDER,
      requestBody: { courseIds: orderedIds }
    });
    setSavingArrangeOrder(false);
    if (error) {
      toast.error(error);
      return;
    }
    const orderMap = new Map(orderedIds.map((id, index) => [id, index + 1]));
    setCourses(prev =>
      [...prev]
        .map(course => {
          const id = String(course._id ?? course.id ?? '');
          const order = orderMap.get(id);
          return order == null ? course : { ...course, order };
        })
        .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
    );
    setShowArrangeModal(false);
    setArrangeCourses([]);
    toast.success('Course order updated');
    if (onReloadList) onReloadList();
  };

  const handleSaveCourseModal = async () => {
    if (!selectedCourse || !editTitle.trim()) return;
    const fd = new FormData();
    fd.append('category', editCategory);
    fd.append('title', editTitle.trim());
    fd.append('description', (editDescription && editDescription.trim()) || ' ');
    fd.append('allowedRoles', JSON.stringify(stripImplicitCourseAccessRoles(selectedRoleIds)));
    if (editCoverFile) fd.append('coverImage', editCoverFile);
    if (editThumbFile) fd.append('thumbnailImage', editThumbFile);
    let r;
    let saveErr;
    try {
      const res = await fetch(
        API_URL + ENDPOINTS.COURSE_WITH_ID(String(selectedCourse._id ?? selectedCourse.id ?? '')),
        {
          method: 'PUT',
          body: fd,
          credentials: 'include'
        }
      );
      const body = await res.json().catch(() => ({}));
      if (res.status < 200 || res.status >= 300) {
        saveErr = body.message || body.error || 'Could not update course';
      } else {
        r = body;
      }
    } catch {
      saveErr = 'Could not update course';
    }
    if (saveErr) {
      toast.error(saveErr);
      return;
    }
    setCourses(prev =>
      prev.map(course =>
        String(course._id ?? course.id ?? '') === String(selectedCourse._id ?? selectedCourse.id ?? '')
          ? {
              ...course,
              title: r.title ?? editTitle.trim(),
              description: r.description ?? editDescription,
              coverImage: r.coverImage != null && r.coverImage !== '' ? r.coverImage : course.coverImage,
              thumbnailImage:
                r.thumbnailImage != null && r.thumbnailImage !== '' ? r.thumbnailImage : course.thumbnailImage,
              allowedRoles: Array.isArray(r.allowedRoles) ? r.allowedRoles : selectedRoleIds
            }
          : course
      )
    );
    closeCourseModal();
    if (onReloadList) onReloadList();
  };

  const confirmDeleteCourse = async () => {
    if (!selectedCourse) return;
    setDeletingCourse(true);
    const { error } = await makeApiRequests({
      method: 'DELETE',
      endpoint: ENDPOINTS.COURSE_WITH_ID(String(selectedCourse._id ?? selectedCourse.id ?? ''))
    });
    setDeletingCourse(false);
    if (error) {
      toast.error(error);
      return;
    }
    const selId = String(selectedCourse._id ?? selectedCourse.id ?? '');
    setCourses(prev => prev.filter(c => String(c._id ?? c.id ?? '') !== selId));
    toast.success('Course deleted');
    setShowDeleteCourseConfirm(false);
    closeCourseModal();
    if (onReloadList) onReloadList();
  };

  const handleCreateCourseSubmit = async () => {
    if (!createTitle.trim()) return;
    setCreatingCourse(true);
    const fd = new FormData();
    fd.append('category', createCategory);
    fd.append('title', createTitle.trim());
    fd.append('description', (createDescription && String(createDescription).trim()) || ' ');
    fd.append('order', String(courses.length + 1));
    fd.append('allowedRoles', JSON.stringify(stripImplicitCourseAccessRoles(createRoles || [])));
    if (createCoverFile) fd.append('coverImage', createCoverFile);
    if (createThumbFile) fd.append('thumbnailImage', createThumbFile);

    let createdBody;
    try {
      const res = await fetch(API_URL + ENDPOINTS.COURSES_BASE, {
        method: 'POST',
        body: fd,
        credentials: 'include'
      });
      const body = await res.json().catch(() => ({}));
      if (res.status < 200 || res.status >= 300) {
        setCreatingCourse(false);
        toast.error(body.message || body.error || 'Could not create course');
        return;
      }
      createdBody = body;
    } catch {
      setCreatingCourse(false);
      toast.error('Could not create course');
      return;
    }

    const cid = String(createdBody._id);
    const modRes = await makeApiRequests({
      method: 'POST',
      endpoint: ENDPOINTS.COURSE_MODULES(cid),
      requestBody: { title: 'New module', description: ' ', order: 1 }
    });
    if (modRes.error) {
      setCreatingCourse(false);
      toast.error(modRes.error);
      return;
    }
    const mid = String(modRes.response._id);
    const lesRes = await makeApiRequests({
      method: 'POST',
      endpoint: ENDPOINTS.COURSE_MODULE_LESSONS(mid),
      requestBody: {
        title: 'New lesson',
        description: ' ',
        order: 1,
        videoUrl: '',
        platform: ''
      }
    });
    if (lesRes.error) {
      setCreatingCourse(false);
      toast.error(lesRes.error);
      return;
    }

    const courseRes = await makeApiRequests({
      method: 'GET',
      endpoint: ENDPOINTS.COURSE_WITH_ID(cid)
    });
    if (courseRes.error) {
      setCreatingCourse(false);
      toast.error(courseRes.error);
      return;
    }
    const modsRes = await makeApiRequests({
      endpoint: ENDPOINTS.COURSE_MODULES_SEARCH(cid),
      requestBody: { sort: { order: 1 } }
    });
    if (modsRes.error) {
      setCreatingCourse(false);
      toast.error(modsRes.error);
      return;
    }
    const modules = modsRes.response.results || [];
    const modulesWithLessons = await Promise.all(
      modules.map(async m => {
        const lesRes2 = await makeApiRequests({
          endpoint: ENDPOINTS.COURSE_MODULE_LESSONS_SEARCH(String(m._id)),
          requestBody: { sort: { order: 1 } }
        });
        return { ...m, lessons: lesRes2.error ? [] : lesRes2.response.results || [] };
      })
    );
    setCreatingCourse(false);

    const full = {
      ...courseRes.response,
      modules: modulesWithLessons,
      allowedRoles: courseRes.response.allowedRoles || []
    };
    const summary = {
      ...full,
      modules: [],
      moduleCount: full.modules?.length ?? 0,
      lessonCount: full?.modules?.length ? full.modules.reduce((a, m) => a + (m.lessons?.length || 0), 0) : 0,
      allowedRoles: full.allowedRoles || []
    };
    setCourses(prev => [...prev, summary]);
    closeCourseModal();
    const newId = String(full._id ?? full.id ?? '');
    if (typeof onManageCourse === 'function') {
      onManageCourse(newId);
    }
    if (onReloadList) onReloadList();
  };

  const deleteCourseConfirmTitle = (editTitle.trim() || selectedCourse?.title || '').trim() || 'this course';

  const isCreateMode = courseModalMode === 'create';
  const isManageMode = courseModalMode === 'manage';

  return (
    <div>
      <AcademyHome courses={courses} listLoading={listLoading} listError={listError}
        onReloadList={onReloadList} listSearchInput={listSearchInput}
        onListSearchInputChange={onListSearchInputChange} canManageCourses={canManageCourses}
        onManageCourse={onManageCourse} onViewCourse={onViewCourse}
        onEditDetails={course => openCourseModal(course, 'details')}
        onCreate={openCreateCourseModal} onArrange={openArrangeModal} />

      <Modal show={showArrangeModal} onHide={closeArrangeModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton className="bg-primary text-white border-0">
          <Modal.Title>
            <div className="font-weight-bold">Arrange courses</div>
            <div className="small font-weight-normal mt-1" style={{ opacity: 0.9 }}>
              Drag and drop course titles to set the display order.
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3" style={{ width: '100%', minHeight: 500 }}>
          {arrangeCourses.length === 0 ? (
            <div className="text-muted small">No courses available.</div>
          ) : (
            <DragDropContext onDragEnd={onArrangeDragEnd}>
              <Droppable droppableId="course-arrange-list">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="border rounded p-2"
                    style={{
                      backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : '#fff',
                      width: '100%',
                      height: 490,
                      minHeight: 490,
                      overflowY: 'auto'
                    }}
                  >
                    {arrangeCourses.map((course, index) => (
                      <Draggable
                        key={String(course._id ?? course.id ?? '')}
                        draggableId={String(course._id ?? course.id ?? '')}
                        index={index}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2"
                            style={{
                              backgroundColor: dragSnapshot.isDragging ? '#f1f3f5' : '#fff',
                              ...dragProvided.draggableProps.style
                            }}
                          >
                            <div className="d-flex align-items-center min-w-0" style={{ gap: '0.65rem' }}>
                              <GripVertical className="text-muted flex-shrink-0" size={16} aria-hidden />
                              <span className="text-muted small font-weight-bold">{index + 1}.</span>
                              <span className="text-truncate">{course.title || 'Untitled course'}</span>
                            </div>
                            {/* <span className="text-muted small">Drag</span> */}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <div className="d-flex w-100 justify-content-end" style={{ gap: 8 }}>
            <Button variant="outline-secondary" onClick={closeArrangeModal} disabled={savingArrangeOrder}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveArrangeCourses}
              disabled={savingArrangeOrder || arrangeCourses.length < 2}
            >
              {savingArrangeOrder ? 'Saving order…' : 'Save order'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={courseModalMode !== null && !showDeleteCourseConfirm}
        onHide={closeCourseModal}
        centered
        scrollable
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-primary text-white border-0">
          <Modal.Title>
            {isCreateMode ? (
              <>
                <div className="font-weight-bold">Create new course</div>
                <div className="small font-weight-normal mt-1" style={{ opacity: 0.9 }}>
                  Add course details, then build modules and lessons in the editor.
                </div>
              </>
            ) : (
              <>
                <div className="font-weight-bold">Manage course</div>
                {selectedCourse ? (
                  <div className="small font-weight-normal mt-1" style={{ opacity: 0.9 }}>
                    {editTitle.trim() || selectedCourse.title}
                  </div>
                ) : null}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isCreateMode ? (
            <>
              <Form.Group>
                <Form.Label>Course title *</Form.Label>
                <Form.Control
                  type="text"
                  value={createTitle}
                  onChange={e => setCreateTitle(e.target.value)}
                  placeholder="Enter course title..."
                />
              </Form.Group>
              <Form.Group>
                  <Form.Label htmlFor="academy-create-topic">Training topic</Form.Label>
                  <Form.Control as="select" id="academy-create-topic" value={createCategory} onChange={e => setCreateCategory(e.target.value)}>
                    <option value="">Automatic from course title and summary</option>
                    {ACADEMY_TOPICS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </Form.Control>
                  <Form.Text className="text-muted">Choose where this course appears in the training hub.</Form.Text>
                </Form.Group>
                <Form.Group>
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={createDescription}
                  onChange={e => setCreateDescription(e.target.value)}
                  placeholder="Enter course description..."
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Cover image</Form.Label>

                <div>
                  {createCoverPreviewUrl ? (
                    <div
                      className="mb-2 w-100 overflow-hidden shadow-sm"
                      style={{ borderRadius: '0.25rem 0.25rem 0 0' }}
                    >
                      <img
                        src={createCoverPreviewUrl}
                        alt="Cover preview"
                        className="w-100 d-block"
                        style={{ height: 88, objectFit: 'cover' }}
                      />
                    </div>
                  ) : null}
                  <input
                    ref={createCoverInputRef}
                    type="file"
                    className="d-none"
                    accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      setCreateCoverFile(f || null);
                      e.target.value = '';
                    }}
                  />
                  <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.75rem' }}>
                    <button
                      type="button"
                      className="d-inline-flex align-items-center"
                      style={{
                        backgroundColor: '#850131',
                        border: 'none',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                        letterSpacing: '0.01em',
                        padding: '0.35rem 0.9rem',
                        borderRadius: '9999px',
                        lineHeight: 1.3,
                        boxShadow: '0 2px 6px rgba(133, 1, 49, 0.22)',
                        cursor: 'pointer'
                      }}
                      onClick={() => createCoverInputRef.current?.click()}
                    >
                      <CloudUpload className="mr-2" size={15} />
                      Choose cover image
                    </button>
                    <span className="small text-muted">JPG, PNG, WEBP…</span>
                    {createCoverFile ? (
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 text-muted small"
                        onClick={() => setCreateCoverFile(null)}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label>Banner / thumbnail</Form.Label>

                <div>
                  {createThumbPreviewUrl ? (
                    <div className="mb-2 overflow-hidden border" style={{ borderRadius: '1rem', maxWidth: 200 }}>
                      <img
                        src={createThumbPreviewUrl}
                        alt="Banner preview"
                        className="w-100"
                        style={{ height: 128, objectFit: 'cover' }}
                      />
                    </div>
                  ) : null}
                  <input
                    ref={createThumbInputRef}
                    type="file"
                    className="d-none"
                    accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      setCreateThumbFile(f || null);
                      e.target.value = '';
                    }}
                  />
                  <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.75rem' }}>
                    <button
                      type="button"
                      className="d-inline-flex align-items-center"
                      style={{
                        backgroundColor: '#850131',
                        border: 'none',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                        letterSpacing: '0.01em',
                        padding: '0.35rem 0.9rem',
                        borderRadius: '9999px',
                        lineHeight: 1.3,
                        boxShadow: '0 2px 6px rgba(133, 1, 49, 0.22)',
                        cursor: 'pointer'
                      }}
                      onClick={() => createThumbInputRef.current?.click()}
                    >
                      <CloudUpload className="mr-2" size={15} />
                      Choose banner image
                    </button>
                    <span className="small text-muted">JPG, PNG, WEBP…</span>
                    {createThumbFile ? (
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 text-muted small"
                        onClick={() => setCreateThumbFile(null)}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Allowed roles</Form.Label>
                <Select
                  isMulti
                  options={courseAccessRoleOptions}
                  value={createRoles.map(id => courseAccessRoleOptions.find(opt => opt.value === id)).filter(Boolean)}
                  onChange={selected => setCreateRoles(selected ? selected.map(opt => opt.value) : [])}
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  menuShouldScrollIntoView={false}
                  placeholder="Select roles..."
                />
              </Form.Group>
            </>
          ) : isManageMode ? (
            <Tabs activeKey={courseModalTab} onSelect={k => k != null && setCourseModalTab(k)} className="mb-0">
              <Tab eventKey="details" title="Course" className="pt-3">
                <Form.Group>
                  <Form.Label>Course title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    placeholder="Course title"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="academy-edit-topic">Training topic</Form.Label>
                  <Form.Control as="select" id="academy-edit-topic" value={editCategory} onChange={e => setEditCategory(e.target.value)}>
                    <option value="">Automatic from course title and summary</option>
                    {ACADEMY_TOPICS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </Form.Control>
                  <Form.Text className="text-muted">Choose where this course appears in the training hub.</Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Description *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    placeholder="Course description"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Cover image</Form.Label>
                  <div>
                    {editCoverPreviewUrl ? (
                      <div
                        className="mb-2 w-100 overflow-hidden shadow-sm"
                        style={{ borderRadius: '0.25rem 0.25rem 0 0' }}
                      >
                        <img
                          src={editCoverPreviewUrl}
                          alt="Cover preview"
                          className="w-100 d-block"
                          style={{ height: 88, objectFit: 'cover' }}
                        />
                      </div>
                    ) : selectedCourse?.coverImage ? (
                      <div className="mb-2 w-100">
                        <CourseImageThumb
                          courseId={String(selectedCourse._id ?? selectedCourse.id ?? '')}
                          raw={selectedCourse.coverImage}
                          alt=""
                          height={88}
                          layout="hero"
                        />
                      </div>
                    ) : null}
                    <input
                      ref={editCoverInputRef}
                      type="file"
                      className="d-none"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        setEditCoverFile(f || null);
                        e.target.value = '';
                      }}
                    />
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.75rem' }}>
                      <button
                        type="button"
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: '#850131',
                          border: 'none',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.8125rem',
                          letterSpacing: '0.01em',
                          padding: '0.35rem 0.9rem',
                          borderRadius: '9999px',
                          lineHeight: 1.3,
                          boxShadow: '0 2px 6px rgba(133, 1, 49, 0.22)',
                          cursor: 'pointer'
                        }}
                        onClick={() => editCoverInputRef.current?.click()}
                      >
                        <CloudUpload className="mr-2" size={15} />
                        Choose cover image
                      </button>
                      <span className="small text-muted">JPG, PNG, WEBP…</span>
                      {editCoverFile ? (
                        <Button
                          type="button"
                          variant="link"
                          className="p-0 text-muted small"
                          onClick={() => setEditCoverFile(null)}
                        >
                          Clear
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>Banner / thumbnail</Form.Label>
                  <div>
                    {editThumbPreviewUrl ? (
                      <div className="mb-2 overflow-hidden border" style={{ borderRadius: '1rem', maxWidth: 200 }}>
                        <img
                          src={editThumbPreviewUrl}
                          alt="Banner preview"
                          className="w-100"
                          style={{ height: 128, objectFit: 'cover' }}
                        />
                      </div>
                    ) : selectedCourse?.thumbnailImage ? (
                      <div className="mb-2" style={{ maxWidth: 200 }}>
                        <CourseImageThumb
                          courseId={String(selectedCourse._id ?? selectedCourse.id ?? '')}
                          raw={selectedCourse.thumbnailImage}
                          alt=""
                          height={128}
                        />
                      </div>
                    ) : null}
                    <input
                      ref={editThumbInputRef}
                      type="file"
                      className="d-none"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        setEditThumbFile(f || null);
                        e.target.value = '';
                      }}
                    />
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.75rem' }}>
                      <button
                        type="button"
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: '#850131',
                          border: 'none',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.8125rem',
                          letterSpacing: '0.01em',
                          padding: '0.35rem 0.9rem',
                          borderRadius: '9999px',
                          lineHeight: 1.3,
                          boxShadow: '0 2px 6px rgba(133, 1, 49, 0.22)',
                          cursor: 'pointer'
                        }}
                        onClick={() => editThumbInputRef.current?.click()}
                      >
                        <CloudUpload className="mr-2" size={15} />
                        Choose banner image
                      </button>
                      <span className="small text-muted">JPG, PNG, WEBP…</span>
                      {editThumbFile ? (
                        <Button
                          type="button"
                          variant="link"
                          className="p-0 text-muted small"
                          onClick={() => setEditThumbFile(null)}
                        >
                          Clear
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Form.Group>
              </Tab>
              <Tab eventKey="access" title="Access" className="pt-3">
                <Form.Group>
                  <Form.Label>Select roles that can access this course</Form.Label>
                  <Select
                    isMulti
                    options={courseAccessRoleOptions}
                    value={selectedRoleOptions}
                    onChange={selected => setSelectedRoleIds(selected ? selected.map(opt => opt.value) : [])}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                    placeholder="Select roles..."
                  />
                </Form.Group>
              </Tab>
            </Tabs>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          {isCreateMode ? (
            <div className="d-flex w-100 justify-content-end flex-wrap" style={{ gap: 8 }}>
              <Button variant="outline-secondary" onClick={closeCourseModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!createTitle.trim() || creatingCourse}
                onClick={handleCreateCourseSubmit}
              >
                {creatingCourse ? 'Creating…' : 'Create course'}
              </Button>
            </div>
          ) : (
            <div className="d-flex w-100 align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
              {canManageCourses ? (
                <Button
                  variant="outline-danger"
                  className="rounded-pill"
                  disabled={deletingCourse}
                  onClick={() => setShowDeleteCourseConfirm(true)}
                >
                  <Trash className="mr-2" size={16} />
                  Delete course
                </Button>
              ) : (
                <span />
              )}
              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                <Button variant="outline-secondary" className="rounded-pill" onClick={closeCourseModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="rounded-pill"
                  disabled={!editTitle.trim() || deletingCourse}
                  onClick={handleSaveCourseModal}
                >
                  Save changes
                </Button>
              </div>
            </div>
          )}
        </Modal.Footer>
      </Modal>

      <AlertModal
        show={showDeleteCourseConfirm}
        alertText={`Delete "${deleteCourseConfirmTitle}"? This permanently removes the course, its modules, and lessons for all users.`}
        dismissButtonText="Cancel"
        dismissButtonVariant="secondary"
        continueButtonText="Delete"
        continueButtonVariant="danger"
        showProgress={deletingCourse}
        progressText="Deleting course…"
        onDismissClick={() => {
          if (!deletingCourse) setShowDeleteCourseConfirm(false);
        }}
        onHide={() => {
          if (!deletingCourse) setShowDeleteCourseConfirm(false);
        }}
        onContinueClick={confirmDeleteCourse}
      />
    </div>
  );
}

export default CourseTrainingCatalog;
